/**
 * Network/auth recovery helpers for Chrome tab freeze.
 *
 * Background tabs can:
 * 1. Hold Web Locks (`lock:domio-auth-token`) while frozen — getSession hangs forever
 * 2. Leave HTTP/2 fetch streams in a zombie state — mutations never settle
 */

import type { SupabaseClient } from "@supabase/supabase-js";
import { TIMEOUT_MESSAGE } from "@/lib/with-timeout";

export const DATA_FETCH_TIMEOUT_MS = 12_000;
export const AUTH_FETCH_TIMEOUT_MS = 15_000;
export const STORAGE_FETCH_TIMEOUT_MS = 90_000;

/** Must stay above DATA_FETCH_TIMEOUT_MS so the AbortController wins first. */
export const WRITE_OPERATION_TIMEOUT_MS = 13_000;
export const REFRESH_SESSION_TIMEOUT_MS = 8_000;

type LockFn = () => Promise<unknown>;

/**
 * Skip navigator.locks. All DOMIO apps share storageKey `domio-auth-token`,
 * so a frozen sibling tab can hold the lock and freeze this one.
 */
export async function noOpAuthLock<R>(
  _name: string,
  _acquireTimeout: number,
  fn: LockFn
): Promise<R> {
  return (await fn()) as R;
}

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

function timeoutMsForUrl(url: string): number {
  if (url.includes("/storage/v1/")) return STORAGE_FETCH_TIMEOUT_MS;
  if (url.includes("/auth/v1/")) return AUTH_FETCH_TIMEOUT_MS;
  return DATA_FETCH_TIMEOUT_MS;
}

function combineAbortSignals(
  a?: AbortSignal | null,
  b?: AbortSignal | null
): AbortSignal | undefined {
  const signals = [a, b].filter((signal): signal is AbortSignal => Boolean(signal));
  if (signals.length === 0) return undefined;
  if (signals.length === 1) return signals[0];
  if (typeof AbortSignal.any === "function") {
    return AbortSignal.any(signals);
  }
  const controller = new AbortController();
  for (const signal of signals) {
    if (signal.aborted) {
      controller.abort(signal.reason);
      break;
    }
    signal.addEventListener("abort", () => controller.abort(signal.reason), {
      once: true,
    });
  }
  return controller.signal;
}

export function createTimedAuthFetch(onUnauthorized: () => void): typeof fetch {
  return async (input, init) => {
    const url = requestUrl(input);
    const timeoutMs = timeoutMsForUrl(url);
    const timeoutController = new AbortController();
    const timeoutId = window.setTimeout(() => {
      timeoutController.abort();
    }, timeoutMs);

    try {
      const response = await fetch(input, {
        ...init,
        signal: combineAbortSignals(init?.signal, timeoutController.signal),
      });

      if (response.status === 401 && !url.includes("/auth/v1/")) {
        console.warn("[Supabase] 401 Unauthorized – przekierowanie do logowania");
        onUnauthorized();
        throw new Error("Sesja wygasła");
      }

      return response;
    } catch (error) {
      const callerAborted = Boolean(init?.signal?.aborted);
      if (timeoutController.signal.aborted && !callerAborted) {
        throw new Error(TIMEOUT_MESSAGE);
      }
      throw error;
    } finally {
      window.clearTimeout(timeoutId);
    }
  };
}

/**
 * Resume token auto-refresh after Chrome unsuspends the tab.
 * Does not call refreshSession on every focus — that used to flash a full-page spinner.
 */
export function installForegroundAuthRecovery(client: SupabaseClient): void {
  if (typeof window === "undefined") return;

  const recover = () => {
    if (typeof document !== "undefined" && document.visibilityState === "hidden") {
      return;
    }
    try {
      client.auth.startAutoRefresh();
    } catch (error) {
      console.warn("[supabase] startAutoRefresh failed after tab wake", error);
    }
  };

  document.addEventListener("visibilitychange", recover);
  window.addEventListener("focus", recover);
  window.addEventListener("pageshow", recover);
}
